/**
 * Health score weights (Danish-approved 2026-06-22):
 * - Tier 1 match rate: 35% (memo discipline -> clean reconciliation)
 * - Memo discipline:   30% (% of QBO $ that have parseable PO#)
 * - Credit Pending $:  20% (lower is better)
 * - Aging:             10% (older credit_pending = worse)
 * - Auto-flip:          5% (heuristic confidence)
 *
 * Each sub-score normalized to 0-1; weighted sum * 100 -> 0-100 score.
 */

export const TARGETS = {
  tier1Rate: 0.80,           // >=80% is good
  memoDiscipline: 0.95,      // >=95% is good
  creditPendingPctOfP: 0.10, // <=10% of P is good
  agingDays: 60,             // <=60 days is fresh
  autoFlipConfidence: 0.90,
};

export const WEIGHTS = {
  tier1Rate: 0.35,
  memoDiscipline: 0.30,
  creditPendingDollars: 0.20,
  creditPendingAge: 0.10,
  autoFlipConfidence: 0.05,
};

export function computeSubMetrics(closeResult, matches) {
  // closeResult.parts has p_cents, a_used_cents, match_tally
  // matches is the list of mec_match rows from listMatches()
  const parts = closeResult?.parts || {};
  const p = Math.max(parts.p_cents || 0, 1); // avoid divide-by-zero
  const tally = parts.match_tally || {};

  const tier1Cents = tally.tier_1_cents || 0;
  const tier3NoPoCents = tally.tier_3_no_po_cents || 0;
  const creditPendingCents = tally.credit_pending_cents || 0;

  // Tier 1 match rate: tier_1 $ / P, clamp to [0,1]
  const tier1Rate = Math.min(tier1Cents / p, 1.0);

  // Memo discipline: 1 - (tier_3_no_po / P)
  const memoDiscipline = Math.max(0, 1 - tier3NoPoCents / p);

  // Credit Pending dollars as % of P (lower is better)
  const creditPendingPctOfP = creditPendingCents / p;

  // Aging: oldest credit-pending row's age in days
  let oldestDate = null;
  if (Array.isArray(matches)) {
    for (const m of matches) {
      const method = m?.matched_method || '';
      const isCreditPending =
        m?.bucket === 'pending_credit' ||
        method.includes('credit_pending') ||
        method.includes('pending_credit') ||
        method === 'warranty_credit_pending';
      if (!isCreditPending) continue;
      const d = m?.qbo_date || m?.matched_date;
      if (d && (!oldestDate || d < oldestDate)) oldestDate = d;
    }
  }
  const today = new Date();
  let agingDays = 0;
  if (oldestDate) {
    const dt = new Date(oldestDate);
    if (!isNaN(dt.getTime())) {
      agingDays = Math.max(0, Math.floor((today - dt) / 86400000));
    }
  }

  // Auto-flip confidence: fraction of Tier 2 matches that are auto-bucketed
  // or confirmed (vs pending or rejected)
  let t2Total = 0;
  let t2Confident = 0;
  if (Array.isArray(matches)) {
    for (const m of matches) {
      if (m?.matched_method !== 'tier_2_heuristic') continue;
      t2Total++;
      if (m?.review_status === 'auto' || m?.review_status === 'confirmed') {
        t2Confident++;
      }
    }
  }
  // 1.0 if no Tier 2 rows (no confidence problem to surface)
  const autoFlipConfidence = t2Total > 0 ? t2Confident / t2Total : 1.0;

  return {
    tier1Rate,
    memoDiscipline,
    creditPendingPctOfP,
    agingDays,
    autoFlipConfidence,
    // Raw numbers for display
    tier1Cents,
    tier3NoPoCents,
    creditPendingCents,
    p_cents: parts.p_cents || 0,
  };
}

export function computeScore(subs) {
  if (!subs) return null;
  // Each sub-score 0-1 against its target.
  const t1Sub = Math.min(subs.tier1Rate / TARGETS.tier1Rate, 1.0);
  const memoSub = Math.min(subs.memoDiscipline / TARGETS.memoDiscipline, 1.0);
  // Credit pending: 1.0 at 0%, 0.0 at target threshold
  const cpDollarsSub = Math.max(
    0,
    1 - subs.creditPendingPctOfP / TARGETS.creditPendingPctOfP
  );
  // Aging: 1.0 if 0 days, 0.0 if at or beyond target
  const agingSub =
    subs.agingDays <= 0
      ? 1.0
      : subs.agingDays >= TARGETS.agingDays
      ? 0.0
      : 1 - subs.agingDays / TARGETS.agingDays;
  const autoSub = Math.min(
    subs.autoFlipConfidence / TARGETS.autoFlipConfidence,
    1.0
  );

  const weighted =
    t1Sub * WEIGHTS.tier1Rate +
    memoSub * WEIGHTS.memoDiscipline +
    cpDollarsSub * WEIGHTS.creditPendingDollars +
    agingSub * WEIGHTS.creditPendingAge +
    autoSub * WEIGHTS.autoFlipConfidence;

  return Math.round(weighted * 100);
}

export function scoreBand(score) {
  if (score === null || score === undefined) return 'neutral';
  if (score >= 80) return 'good';
  if (score >= 60) return 'warn';
  return 'bad';
}

export function topOpportunity(subs) {
  if (!subs) return null;
  const items = [
    {
      key: 'memo',
      label: 'Improve memo discipline',
      delta: TARGETS.memoDiscipline - subs.memoDiscipline,
      hint: 'Train bookkeeper to write `PO: <RO#>` on every QBO entry. Biggest variance lever.',
    },
    {
      key: 'tier1',
      label: 'Improve Tier 1 match rate',
      delta: TARGETS.tier1Rate - subs.tier1Rate,
      hint: 'More QBO bills tagged with `PO:` memos -> more clean Tier 1 matches.',
    },
    {
      key: 'aging',
      label: 'Chase old credit-pending refunds',
      delta: subs.agingDays > TARGETS.agingDays ? 1 : 0,
      hint: `Oldest credit pending is ${subs.agingDays} days old. Chase the vendor.`,
    },
    {
      key: 'credit_pending',
      label: 'Reduce credit pending balance',
      delta: subs.creditPendingPctOfP - TARGETS.creditPendingPctOfP,
      hint: 'Process refunds faster or escalate stuck credits.',
    },
  ];
  // Pick the largest positive gap
  const top = items
    .filter((i) => i.delta > 0)
    .sort((a, b) => b.delta - a.delta)[0];
  return top || null;
}

export default {
  TARGETS,
  WEIGHTS,
  computeSubMetrics,
  computeScore,
  scoreBand,
  topOpportunity,
};
